import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { 
  Zap, Plus, RefreshCw, Play, Pause, CheckCircle2, Clock, 
  AlertTriangle, TrendingUp, TrendingDown, Minus, Users, Target,
  Loader2, ChevronRight, Calendar, ListChecks, BarChart3, ArrowRight
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  draft: 'bg-slate-500',
  pending_approval: 'bg-amber-500',
  approved: 'bg-blue-500',
  in_progress: 'bg-[#00b0ab]/100',
  monitoring: 'bg-violet-500',
  completed: 'bg-emerald-500',
  paused: 'bg-orange-500',
  cancelled: 'bg-red-500'
};

const STAGE_LABELS = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  in_progress: 'In Progress',
  monitoring: 'Monitoring',
  completed: 'Completed',
  paused: 'Paused',
  cancelled: 'Cancelled'
};

const TrendIcon = ({ trend }) => {
  if (trend === 'improving') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
  if (trend === 'declining') return <TrendingDown className="h-4 w-4 text-red-500" />;
  return <Minus className="h-4 w-4 text-slate-400" />;
};

export default function ExecutePIPs() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [pips, setPips] = useState([]);
  const [selectedPIP, setSelectedPIP] = useState(null);
  const [filterStage, setFilterStage] = useState('all');
  
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [progressNote, setProgressNote] = useState('');

  const fetchData = async () => {
    try {
      const [dashRes, pipsRes] = await Promise.all([
        fetch(`${API_URL}/api/execute-pip/dashboard/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/execute-pip?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (dashRes.ok) setDashboard(await dashRes.json());
      if (pipsRes.ok) setPips(await pipsRes.json());
    } catch (error) {
      toast.error('Failed to load PIPs data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success('Data refreshed');
  };

  const handleAdvanceStage = async (pipId, forceStage = null) => {
    setUpdating(true);
    try {
      const url = forceStage 
        ? `${API_URL}/api/execute-pip/${pipId}/advance-stage?force_stage=${forceStage}`
        : `${API_URL}/api/execute-pip/${pipId}/advance-stage`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData();
        if (selectedPIP?.id === pipId) {
          const updated = await fetch(`${API_URL}/api/execute-pip/${pipId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (updated.ok) setSelectedPIP(await updated.json());
        }
      } else {
        const error = await response.json();
        toast.error('Failed to advance', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error advancing stage');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateProgress = async () => {
    if (!newValue || !selectedPIP) return;
    
    setUpdating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/execute-pip/${selectedPIP.id}/update-progress?current_value=${newValue}&note=${encodeURIComponent(progressNote)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success('Progress updated', {
          description: `${result.progress_percent.toFixed(1)}% complete. Trend: ${result.trend}`
        });
        setNewValue('');
        setProgressNote('');
        fetchData();
        const updated = await fetch(`${API_URL}/api/execute-pip/${selectedPIP.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (updated.ok) setSelectedPIP(await updated.json());
      } else {
        const error = await response.json();
        toast.error('Failed to update', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error updating progress');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateTask = async (taskId, status) => {
    if (!selectedPIP) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/execute-pip/${selectedPIP.id}/update-task?task_id=${taskId}&status=${status}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Task updated');
        const updated = await fetch(`${API_URL}/api/execute-pip/${selectedPIP.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (updated.ok) setSelectedPIP(await updated.json());
      }
    } catch (error) {
      toast.error('Error updating task');
    }
  };

  const filteredPIPs = filterStage === 'all' 
    ? pips 
    : pips.filter(p => p.stage === filterStage);

  // Prepare chart data
  const stageChartData = dashboard ? Object.entries(dashboard.stage_counts).map(([stage, count]) => ({
    name: STAGE_LABELS[stage] || stage,
    count,
    fill: STAGE_COLORS[stage]?.replace('bg-', '#').replace('-500', '') || '#6B7280'
  })).filter(d => d.count > 0) : [];

  const CHART_COLORS = ['#10B981', '#F59E0B', '#4F46E5', '#8B5CF6', '#EF4444'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="execute-pips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            Auto-Execute PIPs
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Automated performance improvement lifecycle management
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-200 dark:border-teal-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total PIPs</p>
                  <p className="text-3xl font-bold">{dashboard.total_pips}</p>
                </div>
                <Zap className="h-10 w-10 text-teal-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-[#00b0ab]/30 dark:border-indigo-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active PIPs</p>
                  <p className="text-3xl font-bold">{dashboard.active_count}</p>
                </div>
                <Play className="h-10 w-10 text-[#00b0ab]" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Progress</p>
                  <p className="text-3xl font-bold">{dashboard.average_progress}%</p>
                </div>
                <Target className="h-10 w-10 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${dashboard.overdue_tasks?.length > 0 ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-200' : 'bg-gradient-to-br from-slate-500/10 to-gray-500/10 border-slate-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue Tasks</p>
                  <p className="text-3xl font-bold">{dashboard.overdue_tasks?.length || 0}</p>
                </div>
                <AlertTriangle className={`h-10 w-10 ${dashboard.overdue_tasks?.length > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="list" className="space-y-4">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-3 h-auto">
          <TabsTrigger value="list" className="gap-2">
            <ListChecks className="h-4 w-4" /> PIPs List
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="gap-2">
            <ArrowRight className="h-4 w-4" /> Pipeline
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" /> Analytics
          </TabsTrigger>
        </TabsList>

        {/* PIPs List */}
        <TabsContent value="list">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle>All PIPs</CardTitle>
                <Select value={filterStage} onValueChange={setFilterStage}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    {Object.entries(STAGE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Trend</TableHead>
                    <TableHead>Target Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPIPs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No PIPs found. Convert approved Auto-PIPs to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPIPs.map((pip) => (
                      <TableRow key={pip.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900" onClick={() => { setSelectedPIP(pip); setShowDetailDialog(true); }}>
                        <TableCell className="font-medium">{pip.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{pip.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STAGE_COLORS[pip.stage]} text-white`}>
                            {STAGE_LABELS[pip.stage]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={pip.progress_percent} className="w-20 h-2" />
                            <span className="text-sm">{pip.progress_percent?.toFixed(0)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <TrendIcon trend={pip.trend} />
                        </TableCell>
                        <TableCell>{pip.target_completion_date}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setSelectedPIP(pip); setShowDetailDialog(true); }}
                          >
                            View <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline View */}
        <TabsContent value="pipeline">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {['draft', 'approved', 'in_progress', 'monitoring', 'completed'].map(stage => {
              const stagePips = pips.filter(p => p.stage === stage);
              return (
                <Card key={stage} className="min-h-[300px]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>{STAGE_LABELS[stage]}</span>
                      <Badge variant="secondary">{stagePips.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {stagePips.map(pip => (
                          <div 
                            key={pip.id}
                            className="p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer transition-colors"
                            onClick={() => { setSelectedPIP(pip); setShowDetailDialog(true); }}
                          >
                            <p className="text-sm font-medium truncate">{pip.title}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Progress value={pip.progress_percent} className="flex-1 h-1.5" />
                              <span className="text-xs text-muted-foreground">{pip.progress_percent?.toFixed(0)}%</span>
                            </div>
                          </div>
                        ))}
                        {stagePips.length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-4">No PIPs</p>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>PIPs by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                {stageChartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stageChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          dataKey="count"
                          nameKey="name"
                          label={({ name, count }) => `${name}: ${count}`}
                        >
                          {stageChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Overdue Tasks</CardTitle>
                <CardDescription>Tasks past their due date</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {dashboard?.overdue_tasks?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.overdue_tasks.map((task, idx) => (
                        <div key={idx} className="p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20">
                          <p className="font-medium text-sm">{task.task_title}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            PIP: {task.pip_title}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <Badge variant="destructive" className="text-xs">
                              Due: {task.due_date}
                            </Badge>
                            {task.assigned_to && (
                              <span className="text-xs text-muted-foreground">
                                Assigned: {task.assigned_to}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mb-4 text-emerald-500" />
                      <p>No overdue tasks!</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* PIP Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedPIP && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-teal-500" />
                  {selectedPIP.title}
                </DialogTitle>
                <DialogDescription>
                  {selectedPIP.metric_display_name} • {selectedPIP.category}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Stage and Progress */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Current Stage</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`${STAGE_COLORS[selectedPIP.stage]} text-white`}>
                        {STAGE_LABELS[selectedPIP.stage]}
                      </Badge>
                      {selectedPIP.auto_execute_enabled && (
                        <Badge variant="outline" className="text-teal-600">Auto-Execute</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Overall Progress</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={selectedPIP.progress_percent} className="flex-1" />
                      <span className="font-semibold">{selectedPIP.progress_percent?.toFixed(1)}%</span>
                      <TrendIcon trend={selectedPIP.trend} />
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
                  <div>
                    <p className="text-sm text-muted-foreground">Baseline</p>
                    <p className="text-xl font-bold">{selectedPIP.baseline_value}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Current</p>
                    <p className="text-xl font-bold text-teal-600">{selectedPIP.current_value}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Target</p>
                    <p className="text-xl font-bold text-emerald-600">{selectedPIP.target_value}</p>
                  </div>
                </div>

                {/* Update Progress */}
                {selectedPIP.stage !== 'completed' && selectedPIP.stage !== 'cancelled' && (
                  <div className="p-4 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/20">
                    <Label className="font-semibold">Update Progress</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        placeholder="New value"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        className="w-32"
                      />
                      <Input
                        placeholder="Note (optional)"
                        value={progressNote}
                        onChange={(e) => setProgressNote(e.target.value)}
                        className="flex-1"
                      />
                      <Button onClick={handleUpdateProgress} disabled={updating || !newValue}>
                        {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tasks */}
                {selectedPIP.tasks?.length > 0 && (
                  <div>
                    <Label className="font-semibold">Tasks ({selectedPIP.tasks.filter(t => t.status === 'completed').length}/{selectedPIP.tasks.length} completed)</Label>
                    <div className="space-y-2 mt-2">
                      {selectedPIP.tasks.map((task) => (
                        <div key={task.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                          <Checkbox 
                            checked={task.status === 'completed'}
                            onCheckedChange={(checked) => handleUpdateTask(task.id, checked ? 'completed' : 'pending')}
                          />
                          <div className="flex-1">
                            <p className={`text-sm ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Phase {task.phase} • Due: {task.due_date}
                            </p>
                          </div>
                          <Badge variant={task.status === 'completed' ? 'secondary' : task.due_date < new Date().toISOString().split('T')[0] ? 'destructive' : 'outline'}>
                            {task.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Implementation Plan */}
                {selectedPIP.ai_implementation_plan?.length > 0 && (
                  <div>
                    <Label className="font-semibold">Implementation Plan</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {selectedPIP.ai_implementation_plan.map((phase, idx) => (
                        <div key={idx} className="p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                          <p className="font-medium text-sm">Phase {phase.phase}: {phase.name}</p>
                          <p className="text-xs text-muted-foreground">Weeks {phase.weeks}</p>
                          <ul className="mt-2 space-y-1">
                            {phase.deliverables?.map((d, i) => (
                              <li key={i} className="text-xs flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3 text-teal-500" /> {d}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stage Actions */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Created: {new Date(selectedPIP.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2">
                    {selectedPIP.stage === 'approved' && (
                      <Button onClick={() => handleAdvanceStage(selectedPIP.id, 'in_progress')} disabled={updating}>
                        <Play className="h-4 w-4 mr-2" /> Start Execution
                      </Button>
                    )}
                    {selectedPIP.stage === 'in_progress' && (
                      <Button onClick={() => handleAdvanceStage(selectedPIP.id)} disabled={updating}>
                        <ArrowRight className="h-4 w-4 mr-2" /> Move to Monitoring
                      </Button>
                    )}
                    {selectedPIP.stage === 'monitoring' && (
                      <Button onClick={() => handleAdvanceStage(selectedPIP.id)} disabled={updating} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Complete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
